import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from 'react'
import { useSelector } from 'react-redux'
import { CustomButton, Modal, SvgWarning } from '@ui-kit'
import type {
  EcrmSessionRootState,
  MandatoryPolicyMinutes,
  MandatoryReloginNamespaces,
} from './types'
import {
  broadcastMandatorySoftDismiss,
  broadcastMaxSessionExtension,
  subscribeMandatorySoftDismiss,
  subscribeMaxSessionExtension,
} from './sessionCrossTab'
import {
  getEffectiveSessionElapsedMs,
  useSessionWallClockPauseActive,
} from './sessionTimerPause'
import {
  MANDATORY_SESSION_MAX_EXTENSIONS,
  mandatorySessionExtensionSlotMs,
} from './mandatorySession.constants'

/** Default wait after CTI call ends before mandatory soft/hard modals may appear. */
export const DEFAULT_MANDATORY_RELOGIN_POST_CALL_GRACE_MS = 120_000

const DEFAULT_MAX_MANDATORY_EXTENSIONS = MANDATORY_SESSION_MAX_EXTENSIONS
const DEFAULT_EXTENSION_SLOT_MS = mandatorySessionExtensionSlotMs()

export interface MandatoryReloginManagerProps {
  namespaces: MandatoryReloginNamespaces
  /** When null, mandatory relogin UI is disabled. */
  mandatoryPolicy: MandatoryPolicyMinutes | null
  /** sessionStorage prefix for soft-warning dismiss (per login + extension epoch). */
  warnAckStoragePrefix: string
  onMandatoryLogout: () => void | Promise<void>
  /**
   * Persist a higher max-session extension count (e.g. dispatch Redux). Should be
   * idempotent for the same count across tabs.
   */
  applyMaxSessionExtensionCount: (count: number) => void
  /** How many +{@link extensionSlotMs} extensions are allowed after the base window (default 2). */
  maxMandatoryExtensions?: number
  /** Ms added per extension (from env slot minutes). */
  extensionSlotMs?: number
  /**
   * When true, mandatory modals are suppressed (e.g. CTI call in progress).
   * When the call ends, a post-call grace period runs before modals can show.
   */
  ctiCallActive?: boolean
  /**
   * Ms to wait after `ctiCallActive` becomes false before showing mandatory modals,
   * only if the session was already in the mandatory warning window when the call ended.
   * Defaults to {@link DEFAULT_MANDATORY_RELOGIN_POST_CALL_GRACE_MS}.
   */
  postCallGraceMs?: number
}

function getSoftDismissStorageKey(
  prefix: string,
  sessionLoginAtMs: number,
  extensionCount: number
): string {
  return `${prefix}softDismissed:${sessionLoginAtMs}:${extensionCount}`
}

function readSoftDismissed(
  prefix: string,
  sessionLoginAtMs: number,
  extensionCount: number
): boolean {
  if (sessionLoginAtMs <= 0) return false
  try {
    return (
      sessionStorage.getItem(
        getSoftDismissStorageKey(prefix, sessionLoginAtMs, extensionCount)
      ) === '1'
    )
  } catch {
    return false
  }
}

function writeSoftDismissed(
  prefix: string,
  sessionLoginAtMs: number,
  extensionCount: number
): void {
  if (sessionLoginAtMs <= 0) return
  try {
    sessionStorage.setItem(
      getSoftDismissStorageKey(prefix, sessionLoginAtMs, extensionCount),
      '1'
    )
  } catch {
    /* ignore */
  }
}

/**
 * After a configured wall-clock duration from login (plus optional user extensions),
 * shows a warning modal with logout / dismiss / extend, then a blocking modal at the limit.
 */
export function MandatoryReloginManager({
  namespaces,
  mandatoryPolicy,
  warnAckStoragePrefix,
  onMandatoryLogout,
  applyMaxSessionExtensionCount,
  maxMandatoryExtensions = DEFAULT_MAX_MANDATORY_EXTENSIONS,
  extensionSlotMs = DEFAULT_EXTENSION_SLOT_MS,
  ctiCallActive,
  postCallGraceMs = DEFAULT_MANDATORY_RELOGIN_POST_CALL_GRACE_MS,
}: MandatoryReloginManagerProps): ReactElement | null {
  const isAuthenticated = useSelector(
    (s: EcrmSessionRootState) => s.session.isAuthenticated
  )
  const sessionLoginAtMs = useSelector(
    (s: EcrmSessionRootState) => s.session.sessionLoginAtMs ?? 0
  )
  const maxSessionExtensionCount = useSelector(
    (s: EcrmSessionRootState) => s.session.maxSessionExtensionCount ?? 0
  )
  const durablePause = useSessionWallClockPauseActive(sessionLoginAtMs)
  const pauseActive = Boolean(ctiCallActive) || durablePause

  const maxMinutes = mandatoryPolicy?.maxMinutes ?? 0
  const warnMin = mandatoryPolicy?.warnMinutes ?? 0
  const maxMs = maxMinutes * 60 * 1000
  let warnMs = warnMin * 60 * 1000
  if (maxMs > 0 && warnMs >= maxMs) {
    warnMs = Math.max(60_000, maxMs - 60_000)
  }
  const effectiveMaxMs = maxMs + maxSessionExtensionCount * extensionSlotMs
  const enabled =
    mandatoryPolicy !== null &&
    maxMinutes > 0 &&
    maxMs > 0 &&
    warnMs > 0 &&
    warnMs < maxMs &&
    isAuthenticated &&
    sessionLoginAtMs > 0

  const [showSoft, setShowSoft] = useState(false)
  const [showHard, setShowHard] = useState(false)
  const [postCallGraceUntilMs, setPostCallGraceUntilMs] = useState<
    number | null
  >(null)
  const logoutStartedRef = useRef(false)
  const prevCtiCallActiveRef = useRef(false)

  useEffect(() => {
    logoutStartedRef.current = false
  }, [isAuthenticated, sessionLoginAtMs])

  useEffect(() => {
    setPostCallGraceUntilMs(null)
    prevCtiCallActiveRef.current = false
  }, [sessionLoginAtMs])

  /** After CTI ends, optional grace; cancel grace if a new call starts. */
  useEffect(() => {
    const paused = pauseActive
    if (paused) {
      setPostCallGraceUntilMs(null)
    } else if (prevCtiCallActiveRef.current && !paused) {
      const elapsed = getEffectiveSessionElapsedMs(sessionLoginAtMs)
      const softStart = effectiveMaxMs - warnMs
      if (enabled && sessionLoginAtMs > 0 && elapsed >= softStart) {
        setPostCallGraceUntilMs(Date.now() + postCallGraceMs)
      }
    }
    prevCtiCallActiveRef.current = paused
  }, [
    pauseActive,
    enabled,
    sessionLoginAtMs,
    effectiveMaxMs,
    warnMs,
    postCallGraceMs,
  ])

  /** Sync soft dismiss + extension count across tabs. */
  useEffect(() => {
    if (!enabled || sessionLoginAtMs <= 0) return
    const unsubDismiss = subscribeMandatorySoftDismiss((payload) => {
      if (payload.sessionLoginAtMs !== sessionLoginAtMs) return
      writeSoftDismissed(
        warnAckStoragePrefix,
        sessionLoginAtMs,
        payload.extensionCount
      )
      setShowSoft(false)
    })
    const unsubExt = subscribeMaxSessionExtension((payload) => {
      if (payload.sessionLoginAtMs !== sessionLoginAtMs) return
      applyMaxSessionExtensionCount(payload.extensionCount)
    })
    return () => {
      unsubDismiss()
      unsubExt()
    }
  }, [
    enabled,
    sessionLoginAtMs,
    warnAckStoragePrefix,
    applyMaxSessionExtensionCount,
  ])

  useEffect(() => {
    if (!enabled) {
      setShowSoft(false)
      setShowHard(false)
      return
    }

    const tick = () => {
      const now = Date.now()
      let graceUntil = postCallGraceUntilMs
      if (graceUntil !== null && now >= graceUntil) {
        setPostCallGraceUntilMs(null)
        graceUntil = null
      }
      const inPostCallGrace = graceUntil !== null && now < graceUntil
      const deferForCti = pauseActive || inPostCallGrace

      const elapsed = getEffectiveSessionElapsedMs(sessionLoginAtMs, now)
      if (deferForCti) {
        setShowHard(false)
        setShowSoft(false)
        return
      }
      if (elapsed >= effectiveMaxMs) {
        setShowHard(true)
        setShowSoft(false)
        return
      }
      setShowHard(false)
      const inWarningWindow = elapsed >= effectiveMaxMs - warnMs
      const softDismissed = readSoftDismissed(
        warnAckStoragePrefix,
        sessionLoginAtMs,
        maxSessionExtensionCount
      )
      setShowSoft(inWarningWindow && !softDismissed)
    }

    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [
    enabled,
    effectiveMaxMs,
    warnMs,
    sessionLoginAtMs,
    warnAckStoragePrefix,
    pauseActive,
    postCallGraceUntilMs,
    maxSessionExtensionCount,
  ])

  const dismissSoft = useCallback(() => {
    if (sessionLoginAtMs > 0) {
      writeSoftDismissed(
        warnAckStoragePrefix,
        sessionLoginAtMs,
        maxSessionExtensionCount
      )
      broadcastMandatorySoftDismiss(sessionLoginAtMs, maxSessionExtensionCount)
    }
    setShowSoft(false)
  }, [sessionLoginAtMs, warnAckStoragePrefix, maxSessionExtensionCount])

  const extendSession = useCallback(() => {
    if (sessionLoginAtMs <= 0) return
    const next = Math.min(maxMandatoryExtensions, maxSessionExtensionCount + 1)
    if (next <= maxSessionExtensionCount) return
    applyMaxSessionExtensionCount(next)
    broadcastMaxSessionExtension(sessionLoginAtMs, next)
    setShowSoft(false)
  }, [
    sessionLoginAtMs,
    maxMandatoryExtensions,
    maxSessionExtensionCount,
    applyMaxSessionExtensionCount,
  ])

  const handleMandatoryLogout = useCallback(() => {
    if (logoutStartedRef.current) return
    logoutStartedRef.current = true
    void onMandatoryLogout()
  }, [onMandatoryLogout])

  if (!enabled) {
    return null
  }

  const canExtend = maxSessionExtensionCount < maxMandatoryExtensions

  return (
    <>
      <Modal
        isOpen={showSoft}
        closeModal={dismissSoft}
        showCloseButton
        closeModalOnOutsideClick={false}
        allowOutsideClick={false}
        preventBackgroundInteraction={false}
        width="w-full max-w-md"
        bodyCssClassName="!p-0"
      >
        <div className="px-10 pt-10 pb-8 pr-12 flex flex-col items-center text-center border-b border-accent-200">
          <div className="h-12 w-12 flex items-center justify-center rounded-full bg-warning-100 mb-4">
            <SvgWarning height="1.5rem" width="1.5rem" />
          </div>
          <h2 className="text-base font-medium text-accent-800">
            {namespaces.softTitle}
          </h2>
          <p className="mt-3 text-xs text-accent-600 font-light leading-relaxed">
            {namespaces.softBody}
          </p>
        </div>
        <div className="px-6 py-5 flex flex-row flex-wrap justify-center items-center gap-3">
          <CustomButton
            type="button"
            isFilled
            color="primary"
            onClick={handleMandatoryLogout}
            dataQaId="mandatory-relogin-logout-now"
          >
            <span className="text-xs whitespace-nowrap">
              {namespaces.logoutNowButton}
            </span>
          </CustomButton>
          {canExtend ? (
            <CustomButton
              type="button"
              isFilled={false}
              color="primary"
              onClick={extendSession}
              dataQaId="mandatory-relogin-extend-session"
            >
              <span className="text-xs whitespace-nowrap">
                {namespaces.extendSessionButton}
              </span>
            </CustomButton>
          ) : null}
        </div>
      </Modal>

      <Modal
        isOpen={showHard}
        closeModal={() => {
          /* must use Sign out */
        }}
        showCloseButton={false}
        closeModalOnOutsideClick={false}
        allowOutsideClick={false}
        preventBackgroundInteraction
        width="w-full max-w-md"
      >
        <div className="px-12 py-10 flex flex-col items-center text-center border-b border-accent-200">
          <div className="h-12 w-12 flex items-center justify-center rounded-full bg-warning-100 mb-4">
            <SvgWarning height="1.5rem" width="1.5rem" />
          </div>
          <h2 className="text-base font-medium text-accent-800">
            {namespaces.hardTitle}
          </h2>
          <p className="mt-3 text-xs text-accent-600 font-light leading-relaxed">
            {namespaces.hardBody}
          </p>
        </div>
        <div className="p-6 flex justify-center">
          <CustomButton
            type="button"
            isFilled
            color="primary"
            onClick={handleMandatoryLogout}
            dataQaId="mandatory-relogin-logout"
          >
            <span className="text-xs">{namespaces.logoutButton}</span>
          </CustomButton>
        </div>
      </Modal>
    </>
  )
}
